import { Suspense } from 'react';
import { Helmet } from 'react-helmet';
import { Outlet, useMatches } from 'react-router';
import { ThemeProvider } from 'styled-components';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import ApplicationSync from '/business/shared/components/functional/ApplicationSync';
import AuthChecker from '/business/shared/components/functional/AuthChecker';
import SessionRefresh from '/business/shared/components/functional/SessionRefresh';

import GlobalStyle from '/shared/styled/GlobalStyle/GlobalStyle';

import ModalContainer from '/shared/components/widgets/modal/ModalContainer';
import CookieWarning from '/business/shared/components/widgets/cookie-warning/CookieWarning';
import DisabledOverlay from '/business/shared/components/widgets/disabled-overlay/DisabledOverlay';
import modals from '/business/shared/components/modals/modals';

import { colourTheme } from '/shared/styled/Colours';

import FetchApplicationMeta from '/business/shared/components/functional/FetchApplicationMeta';
import FetchInitial from '/business/shared/components/functional/FetchInitial';
import UserNotifications from '/business/shared/components/widgets/user-notifications/UserNotifications';

import { target } from '/shared/util/Statics';
import type { RootState } from '/business/shared/state/store';

type RouteHandle = {
  syncApplication?: boolean;
};

type RouteHandleInput = ReturnType<typeof useMatches>[number]['handle'];

const isRouteHandle = (handle: RouteHandleInput): handle is RouteHandle => {
  return !!handle && typeof handle === 'object';
};

function RootLayout() {
  const isDebug = useSelector((state: RootState) => state.settings?.isDebug);
  const matches = useMatches();

  const { t } = useTranslation();

  const deepestMatch = matches[matches.length - 1];
  const routeHandle = isRouteHandle(deepestMatch?.handle) ? deepestMatch.handle : null;
  const syncApplication = routeHandle?.syncApplication === true;

  return (
    <>
      <ThemeProvider theme={{ Colours: colourTheme, isDebug, target }}>
        <Helmet titleTemplate={`%s | ${t('business:site.title', 'Carbon Calculator')}`} />
        <AuthChecker />
        <SessionRefresh />
        <FetchInitial />
        <FetchApplicationMeta />
        <ApplicationSync enabled={syncApplication} />
        <DisabledOverlay />
        <CookieWarning />
        <GlobalStyle />
        <Suspense fallback={null}>
          <Outlet />
        </Suspense>
        <UserNotifications />
        <ModalContainer modals={modals} />
      </ThemeProvider>
    </>
  );
}

export default RootLayout;
